import React, { useState, useEffect, useRef } from 'react';
import { 
    Users, Search, Edit3, Trash2, Shield, ShieldCheck, Plus, Save, X, UserCog, 
    Eye, EyeOff, UserCheck, ShieldAlert, Globe, BarChart3, Users2, Building2,
    ShieldX, Lock, Unlock, Settings, User, Layers
} from 'lucide-react';
import * as db from '../lib/supabase-service';
import { 
    normalize, sanitizeAccessibleModules,
    parseModulePermission, ALL_MODULES, getUserModules
} from '../lib/shared-logic';
import { cn } from './SharedComponents';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast } from '../App_V3';
import { hasCapability, normalizeRole } from '../lib/rbac-capabilities';
import {
    buildGovUserUpdateChanges,
    classifyGovUserAccessibleInstances,
    GovUserUpdateValidationError,
    normalizeGovUserAccessibleInstances,
} from '../lib/gov-user-update';
import { canDeleteGovUser } from '../lib/gov-user-deletion';
import {
    downgradeEditorModulePermissions,
    hasViewerModulePermissionConflict,
} from '../lib/module-authorization';

const UserManagementModule = ({ user }: { user: any }) => {
    const canViewUsers = hasCapability(user?.role, 'users.view');
    const normalizedActorRole = normalizeRole(user?.role);
    const canMutateUsers = normalizedActorRole === 'SUPER_ADMIN' || normalizedActorRole === 'ADMINISTRADOR';
    const canCreateUser = canMutateUsers;
    const canEditUser = canMutateUsers;
    const canAssignRoles = canMutateUsers;
    const canAssignScope = canMutateUsers;
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [readError, setReadError] = useState<string | null>(null);
    const [showAdd, setShowAdd] = useState(false);
    const [editUser, setEditUser] = useState<any>(null);
    const [newUser, setNewUser] = useState({ 
        name: '', email: '', role: 'VISOR' as any, password: '', confirmPassword: '', 
        vicepresidency_id: null as string | null, commission_id: null as string | null, 
        accessible_modules: [] as string[], org_scope: 'INSTANCE' as string, 
        accessible_instances: [] as string[], accessible_vps: [] as string[] 
    });
    const [vps, setVps] = useState<any[]>([]);
    const [comms, setComms] = useState<any[]>([]);
    const [instanceCatalogLoaded, setInstanceCatalogLoaded] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [originalUser, setOriginalUser] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingUserIds, setDeletingUserIds] = useState<Set<string>>(new Set());
    const createIdempotencyKeyRef = useRef<string | null>(null);
    const updateIdempotencyKeyRef = useRef<string | null>(null);
    const instancesScopeTouchedRef = useRef(false);
    const vpsScopeTouchedRef = useRef(false);
    const deleteIdempotencyKeysRef = useRef<Map<string, string>>(new Map());
    const deleteInFlightIdsRef = useRef<Set<string>>(new Set());
    const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
    const [lastActiveElement, setLastActiveElement] = useState<Element | null>(null);
    const activeUserDraft = editUser || newUser;
    const isViewerRole = normalizeRole(activeUserDraft?.role) === 'VISOR';
    const hasModulePermissionConflict = hasViewerModulePermissionConflict(
        activeUserDraft?.role,
        activeUserDraft?.accessible_modules,
    );

    const isNewUserDirty = () => {
        return newUser.name !== '' || 
               newUser.email !== '' || 
               newUser.password !== '' || 
               newUser.confirmPassword !== '' || 
               newUser.vicepresidency_id !== null || 
               newUser.commission_id !== null || 
               newUser.accessible_modules.length > 0 || 
               newUser.org_scope !== 'INSTANCE' || 
               newUser.accessible_instances.length > 0 || 
               newUser.accessible_vps.length > 0;
    };

    const isEditUserDirty = () => {
        if (!editUser || !originalUser) return false;
        if (instancesScopeTouchedRef.current) return true;
        if (vpsScopeTouchedRef.current) return true;
        const keys = ['name', 'email', 'role', 'org_scope', 'vicepresidency_id', 'commission_id', 'password', 'confirmPassword'];
        for (const key of keys) {
            if (editUser[key] !== originalUser[key]) return true;
        }
        const arrayCompare = (a: any[] | null | undefined, b: any[] | null | undefined) => {
            const arrA = a || [];
            const arrB = b || [];
            if (arrA.length !== arrB.length) return true;
            const sortedA = [...arrA].sort();
            const sortedB = [...arrB].sort();
            return sortedA.some((val, index) => val !== sortedB[index]);
        };
        if (arrayCompare(editUser.accessible_modules, originalUser.accessible_modules)) return true;
        if (arrayCompare(editUser.accessible_instances, originalUser.accessible_instances)) return true;
        if (arrayCompare(editUser.accessible_vps, originalUser.accessible_vps)) return true;

        return false;
    };

    const closeModal = () => {
        setShowAdd(false);
        setEditUser(null);
        setOriginalUser(null);
        setShowConfirmDiscard(false);
        instancesScopeTouchedRef.current = false;
        vpsScopeTouchedRef.current = false;
        if (lastActiveElement) {
            (lastActiveElement as HTMLElement).focus();
        }
    };

    const handleCloseAttempt = () => {
        if (isSaving) return;
        const isDirty = editUser ? isEditUserDirty() : isNewUserDirty();
        if (isDirty) {
            setShowConfirmDiscard(true);
        } else {
            closeModal();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleCloseAttempt();
        }
    };

    const handleModalKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab') {
            const modalEl = e.currentTarget;
            const focusableEls = modalEl.querySelectorAll(
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
            );
            if (focusableEls.length === 0) return;
            const firstEl = focusableEls[0] as HTMLElement;
            const lastEl = focusableEls[focusableEls.length - 1] as HTMLElement;
            if (e.shiftKey) {
                if (document.activeElement === firstEl) {
                    lastEl.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastEl) {
                    firstEl.focus();
                    e.preventDefault();
                }
            }
        }
    };

    useEffect(() => {
        if (showAdd || editUser) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showAdd, editUser]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showConfirmDiscard) {
                    setShowConfirmDiscard(false);
                } else if (showAdd || editUser) {
                    handleCloseAttempt();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showAdd, editUser, newUser, editUser, isSaving, showConfirmDiscard, originalUser]);

    const refresh = async (): Promise<any[] | null> => {
        setLoading(true);
        setInstanceCatalogLoaded(false);
        try {
            const u = await db.getUsers();
            const h = await db.getHierarchy(user);
            // La jerarquía está filtrada por el actor; la edición de Usuarios
            // necesita resolver accesos históricos contra el catálogo completo.
            const availableInstanceCatalog = (await db.getAllCommissions())
                .filter((commission) => String(commission.name || '').trim().toUpperCase() !== 'DEMO');
            setInstanceCatalogLoaded(true);
            const normalizedUsers = u.map((uEntry) => ({
                ...uEntry,
                accessible_modules: Array.isArray(uEntry.accessible_modules)
                    ? sanitizeAccessibleModules(uEntry.accessible_modules)
                    : uEntry.accessible_modules,
                accessible_instances: normalizeGovUserAccessibleInstances(
                    uEntry.accessible_instances,
                    availableInstanceCatalog,
                ),
            }));
            setUsers(normalizedUsers);

            setVps(h.vicepresidencies || []);
            setComms(availableInstanceCatalog);
            setReadError(null);
            return normalizedUsers;
        } catch (e: unknown) {
            setInstanceCatalogLoaded(false);
            console.error("[USER_MGMT] Refresh error:", e);
            setReadError(db.getScopedReadErrorMessage(e));
            showToast("Error al cargar datos", "error");
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleRoleChange = (newRole: string) => {
        const currentDraft = editUser || newUser;
        const isEditorToViewer = normalizeRole(currentDraft?.role) === 'EDITOR'
            && normalizeRole(newRole) === 'VISOR';
        const editorModulesWouldBeDowngraded = hasViewerModulePermissionConflict(
            'VISOR',
            currentDraft?.accessible_modules,
        );

        if (isEditorToViewer && editorModulesWouldBeDowngraded) {
            const confirmed = window.confirm(
                'Cambiar el rol general a Visor convertirá los permisos Editor de todos los módulos a Visor. ¿Deseas continuar?',
            );
            if (!confirmed) return;
            const accessibleModules = downgradeEditorModulePermissions(currentDraft.accessible_modules);
            if (editUser) setEditUser({ ...editUser, role: newRole, accessible_modules: accessibleModules });
            else setNewUser({ ...newUser, role: newRole as any, accessible_modules: accessibleModules });
            return;
        }

        if (editUser) setEditUser({ ...editUser, role: newRole });
        else setNewUser({ ...newUser, role: newRole as any });
    };

    const handleCreate = async () => {
        if (isSaving) return;
        if (hasViewerModulePermissionConflict(newUser.role, newUser.accessible_modules)) {
            showToast('Resuelve los permisos Editor incompatibles con el rol Visor antes de guardar', 'error');
            return;
        }
        if (!canCreateUser) {
            showToast("No tienes permisos para crear usuarios", "error");
            return;
        }
        setIsSaving(true);
        try {
            if (!newUser.name || !newUser.email) {
                showToast("Nombre y Email son requeridos", "warning");
                setIsSaving(false);
                return;
            }
            if (!newUser.password) {
                showToast("La contraseña es requerida para nuevos usuarios", "warning");
                setIsSaving(false);
                return;
            }
            if (newUser.password !== newUser.confirmPassword) {
                showToast("Las contraseñas no coinciden", "error");
                setIsSaving(false);
                return;
            }

            const payload = { ...newUser };
            delete (payload as any).confirmPassword;

            const idempotencyKey = createIdempotencyKeyRef.current || db.createMutationIdempotencyKey();
            createIdempotencyKeyRef.current = idempotencyKey;
            await db.createUser(payload, { idempotencyKey });
            createIdempotencyKeyRef.current = null;

            showToast("Usuario creado con éxito");
            closeModal();
            refresh();
        } catch (e: unknown) {
            showToast(db.getMutationErrorMessage(e), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (isSaving) return;
        if (hasViewerModulePermissionConflict(editUser?.role, editUser?.accessible_modules)) {
            showToast('Resuelve los permisos Editor incompatibles con el rol Visor antes de guardar', 'error');
            return;
        }
        if (!canEditUser) {
            showToast("No tienes permisos para editar usuarios", "error");
            return;
        }
        setIsSaving(true);
        try {
            if (editUser.password && editUser.password !== editUser.confirmPassword) {
                showToast("Las contraseñas no coinciden", "error");
                setIsSaving(false);
                return;
            }

            const originalInstances = Array.isArray(originalUser?.accessible_instances)
                ? originalUser.accessible_instances
                : [];
            const currentInstances = Array.isArray(editUser?.accessible_instances)
                ? editUser.accessible_instances
                : [];
            const originalVps = Array.isArray(originalUser?.accessible_vps)
                ? originalUser.accessible_vps
                : [];
            const currentVps = Array.isArray(editUser?.accessible_vps)
                ? editUser.accessible_vps
                : [];
            const accessibleVpsChanged = vpsScopeTouchedRef.current
                && JSON.stringify([...currentVps].map(String).sort())
                    !== JSON.stringify([...originalVps].map(String).sort());

            // Catálogo disponible si comms tiene entradas (fuente de verdad visible en el modal)
            const catalogReady = Array.isArray(comms) && comms.length > 0;

            // Construir el conjunto canónico de instancias seleccionadas:
            // solo UUIDs que existen en comms, sin huérfanos históricos ni DEMO.
            const canonicalSelectedInstances = catalogReady
                ? normalizeGovUserAccessibleInstances(currentInstances, comms)
                : [];

            // Comparar contra las instancias originales también normalizadas contra comms
            // para que los UUID huérfanos del registro no generen un falso cambio.
            const canonicalOriginalInstances = catalogReady
                ? normalizeGovUserAccessibleInstances(originalInstances, comms)
                : [...originalInstances];

            const accessibleInstancesChanged = instancesScopeTouchedRef.current
                && JSON.stringify([...canonicalSelectedInstances].sort())
                    !== JSON.stringify([...canonicalOriginalInstances].sort());

            // Bloquear únicamente si: el catálogo no cargó, o el operador añadió
            // una selección nueva que no existe en comms (nunca por UUID histórico).
            if (accessibleInstancesChanged && !catalogReady) {
                showToast('No se puede guardar el alcance: el catálogo de instancias no está disponible.', 'error');
                setIsSaving(false);
                return;
            }

            // Si el catálogo cargó, verificar solo selecciones nuevas del operador.
            if (accessibleInstancesChanged && catalogReady) {
                const commsIds = new Set(
                    (comms as Array<{ id?: unknown }>).map((c) => String(c.id || '').toLowerCase()).filter(Boolean),
                );
                const newSelectionsInvalid = canonicalSelectedInstances.some((id) => !commsIds.has(id.toLowerCase()));
                if (newSelectionsInvalid) {
                    showToast('No se puede guardar el alcance: hay selecciones que no pertenecen al catálogo activo.', 'error');
                    setIsSaving(false);
                    return;
                }
            }

            // Sustituir en el draft las instancias con el conjunto canónico antes de buildGovUserUpdateChanges
            const editUserWithCanonicalInstances = accessibleInstancesChanged && catalogReady
                ? { ...editUser, accessible_instances: canonicalSelectedInstances }
                : editUser;

            const updates = buildGovUserUpdateChanges(
                editUserWithCanonicalInstances,
                originalUser || {},
                accessibleInstancesChanged && catalogReady
                    ? { accessibleInstanceCatalog: comms, accessibleVpCatalog: vps }
                    : accessibleVpsChanged
                        ? { accessibleVpCatalog: vps }
                        : {},
                instancesScopeTouchedRef.current,
                vpsScopeTouchedRef.current,
            );
            if (Object.keys(updates).length === 0) {
                showToast("No hay cambios por guardar", "warning");
                setIsSaving(false);
                return;
            }

            const idempotencyKey = updateIdempotencyKeyRef.current || db.createMutationIdempotencyKey();
            updateIdempotencyKeyRef.current = idempotencyKey;
            const updatedUser = await db.updateUser(editUser.id, updates, { idempotencyKey });
            if (updatedUser.id !== editUser.id) throw new Error('Invalid mutation response');
            const refreshedUsers = await refresh();
            if (!refreshedUsers || !refreshedUsers.some((entry) => entry.id === editUser.id)) {
                throw new Error('Update was not confirmed by scoped read');
            }
            updateIdempotencyKeyRef.current = null;
            showToast("Usuario actualizado");
            closeModal();
        } catch (e: unknown) {
            showToast(
                e instanceof GovUserUpdateValidationError ? e.message : db.getMutationErrorMessage(e),
                "error",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (target: any) => {
        const id = String(target?.id || '');
        if (!canDeleteGovUser(user, target, users)) {
            showToast("No tienes permisos para eliminar usuarios", "error");
            return;
        }
        if (deleteInFlightIdsRef.current.has(id)) return;
        const targetName = String(target?.name || 'Usuario sin nombre');
        const targetEmail = String(target?.email || 'Correo no disponible');
        if (!confirm(`¿Eliminar permanentemente a ${targetName} (${targetEmail})?\n\nEsta acción no se puede deshacer.`)) return;
        deleteInFlightIdsRef.current.add(id);
        setDeletingUserIds((current) => new Set(current).add(id));
        try {
            const idempotencyKey = deleteIdempotencyKeysRef.current.get(id) || db.createMutationIdempotencyKey();
            deleteIdempotencyKeysRef.current.set(id, idempotencyKey);
            const deletedId = await db.deleteUser(id, { idempotencyKey });
            if (deletedId !== id) throw new Error('Invalid mutation response');
            const refreshedUsers = await refresh();
            if (!refreshedUsers || refreshedUsers.some((entry) => entry.id === id)) {
                throw new Error('Deletion was not confirmed by scoped read');
            }
            deleteIdempotencyKeysRef.current.delete(id);
            showToast(`Usuario eliminado: ${targetName}`);
        } catch (e: unknown) {
            showToast(db.getMutationErrorMessage(e), "error");
        } finally {
            deleteInFlightIdsRef.current.delete(id);
            setDeletingUserIds((current) => {
                const next = new Set(current);
                next.delete(id);
                return next;
            });
        }
    };

    const filteredUsers = users.filter(u => 
        u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.role?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading && users.length === 0) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
            <p className="text-emerald-500 font-black uppercase tracking-widest animate-pulse text-xs">Sincronizando Usuarios...</p>
        </div>
    );

    if (readError && users.length === 0) return (
        <div className="min-h-[420px] flex items-center justify-center p-6">
            <div className="max-w-xl w-full p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
                <ShieldAlert className="mx-auto text-red-400" size={36} />
                <h2 className="text-red-200 font-black uppercase tracking-widest">No se pudieron cargar los usuarios</h2>
                <p className="text-white/70 text-sm">{readError}</p>
                <button onClick={() => refresh()} className="px-5 py-2 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-widest">Reintentar</button>
            </div>
        </div>
    );

    return (
        <div className="space-y-10 animate-in fade-in duration-700">
            {readError && (
                <div className="p-4 rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 text-sm font-bold">
                    {readError} Se conservan los últimos usuarios visibles.
                </div>
            )}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <h1 className="text-4xl font-black text-white uppercase mb-2 tracking-tighter">Gestión de Usuarios</h1>
                    <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em]">Panel de Control de Accesos IAPRIORI</p>
                </div>
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="relative flex-grow md:w-64">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                        <input 
                            type="text" 
                            placeholder="BUSCAR USUARIO..." 
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-[10px] font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all placeholder:text-white/10"
                        />
                    </div>
                    {canCreateUser && (
                        <button onClick={() => { setLastActiveElement(document.activeElement); setShowAdd(true); setNewUser({ name: '', email: '', role: 'VISOR', password: '', confirmPassword: '', vicepresidency_id: null, commission_id: null, accessible_modules: [], org_scope: 'INSTANCE', accessible_instances: [], accessible_vps: [] }); }} className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 min-h-[44px] hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-900/20 whitespace-nowrap">
                            <Plus size={16} /> Nuevo Usuario
                        </button>
                    )}
                </div>
            </header>

            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[900px]">
                    <thead>
                        <tr className="bg-slate-950/50 border-b border-slate-800/50">
                            <th className="px-8 py-6 text-[10px] font-black text-white/70 uppercase tracking-widest">Nombre y Vinculación</th>
                            <th className="px-8 py-6 text-[10px] font-black text-white/70 uppercase tracking-widest">Email Directo</th>
                            <th className="px-8 py-6 text-[10px] font-black text-white/70 uppercase tracking-widest">Rol & Alcance</th>
                            <th className="px-8 py-6 text-[10px] font-black text-white/70 uppercase tracking-widest">Módulos Activos</th>
                            <th className="px-8 py-6 text-[10px] font-black text-white/70 uppercase tracking-widest text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/30">
                        {filteredUsers.map(u => (
                            <tr key={u.id} className="hover:bg-slate-800/30 transition-all group">
                                <td className="px-8 py-6">
                                    <span className="text-sm font-black text-white uppercase block leading-none">{u.name}</span>
                                    <span className="text-[9px] text-emerald-500 font-bold uppercase mt-1.5 block">
                                        {u.vicepresidency_id ? `VP: ${vps.find(v => v.id === u.vicepresidency_id)?.name || 'Desconocida'}` : 
                                         u.commission_id ? `INST: ${comms.find(c => c.id === u.commission_id)?.name || 'Desconocida'}` : 
                                         u.org_scope === 'GLOBAL' ? 'Director Nacional' : 'Corporativo'}
                                    </span>
                                </td>
                                <td className="px-8 py-6 text-sm font-bold text-white/60">{u.email}</td>
                                <td className="px-8 py-6">
                                    <div className="flex flex-col gap-1.5">
                                        <span className={cn(
                                            "px-4 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border transition-all inline-block w-fit",
                                            u.role === 'ADMINISTRADOR' || u.role === 'SUPER_ADMIN' ? "bg-purple-500/10 text-purple-500 border-purple-500/30 shadow-[0_0_15px_-5px_rgba(168,85,247,0.4)]" :
                                            u.role === 'EDITOR' ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" :
                                            "bg-slate-800/50 text-white/70 border-slate-700/50"
                                        )}>{u.role}</span>
                                        <span className={cn(
                                            "px-3 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest border inline-block w-fit",
                                            (u.org_scope || 'INSTANCE') === 'GLOBAL' ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" :
                                            (u.org_scope || 'INSTANCE') === 'VP' ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" :
                                            (u.org_scope || 'INSTANCE') === 'COORDINATOR' ? "bg-pink-500/10 text-pink-400 border-pink-500/20" :
                                            "bg-amber-500/10 text-amber-400 border-amber-500/20"
                                        )}>
                                            {(u.org_scope || 'INSTANCE') === 'GLOBAL' ? '🌍 Global' : 
                                             (u.org_scope || 'INSTANCE') === 'VP' ? '📊 VP' : 
                                             (u.org_scope || 'INSTANCE') === 'COORDINATOR' ? '🤝 Coordinador' : '🏢 Instancia'}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                                        {getUserModules(u).slice(0, 6).map((mod: string) => (
                                            <span key={mod} className="text-[7px] bg-slate-800/80 text-white/60 px-2 py-0.5 rounded uppercase font-black tracking-tighter border border-white/5">{mod}</span>
                                        ))}
                                        {getUserModules(u).length > 6 && (
                                            <span className="text-[7px] text-white/30 font-black">+{getUserModules(u).length - 6}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-8 py-6 text-right">
                                    {canEditUser ? (
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => { setLastActiveElement(document.activeElement); setEditUser({ ...u, password: '', confirmPassword: '' }); setOriginalUser({ ...u, password: '', confirmPassword: '' }); setShowPassword(false); }} className="p-3 bg-slate-800/50 text-white/40 hover:text-emerald-500 hover:bg-emerald-500/10 rounded-xl transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"><Edit3 size={16} /></button>
                                            {canDeleteGovUser(user, u, users) && (
                                                <button disabled={deletingUserIds.has(u.id)} onClick={() => handleDelete(u)} aria-label={`Eliminar permanentemente a ${u.name}`} className="p-3 bg-slate-800/50 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all min-w-[44px] min-h-[44px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={16} /></button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex justify-end gap-2 opacity-50 cursor-not-allowed text-white/20">
                                            <Lock size={14} className="mr-2" /> <span className="text-[9px] uppercase font-black">Lectura</span>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredUsers.length === 0 && (
                    <div className="p-20 text-center flex flex-col items-center">
                        <Users size={48} className="text-white/10 mb-4" />
                        <p className="text-white/20 font-black uppercase text-xs tracking-widest">No se encontraron usuarios</p>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {(showAdd || editUser) && (
                    <div 
                        className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-4 md:p-6"
                        onClick={handleBackdropClick}
                    >
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                            onKeyDown={handleModalKeyDown}
                            onClick={e => e.stopPropagation()}
                            className="bg-slate-950 border border-slate-800 w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative flex flex-col max-h-[90dvh] md:max-h-[85dvh]"
                        >
                            {/* Header */}
                            <div className="p-8 md:p-10 pb-4 border-b border-slate-800/50 flex-shrink-0 relative">
                                <button 
                                    onClick={handleCloseAttempt} 
                                    disabled={isSaving}
                                    aria-label="Cerrar modal"
                                    className="absolute top-8 right-8 text-slate-600 hover:text-white transition-colors w-11 h-11 hover:bg-white/5 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <X size={24} />
                                </button>
                                <h3 className="text-white font-black uppercase text-2xl mb-2 tracking-tighter">{editUser ? 'Editar' : 'Registrar'} Usuario</h3>
                                <p className="text-slate-600 text-[10px] font-black uppercase tracking-[0.3em] border-l-2 border-emerald-500 pl-4">Configuración de Accesos Estratégicos IAPRIORI</p>
                            </div>

                            {/* Body (Scrollable) */}
                            <div className="p-8 md:p-10 py-6 overflow-y-auto flex-grow space-y-6 scrollbar-thin scrollbar-thumb-emerald-500/20">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest flex items-center gap-2"><User size={10} /> Nombre Completo</label>
                                        <input
                                            autoFocus
                                            className="w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-white outline-none focus:border-emerald-500 focus:ring-4 ring-emerald-500/10 transition-all font-bold text-sm"
                                            value={editUser ? editUser.name : newUser.name}
                                            onChange={e => editUser ? setEditUser({ ...editUser, name: e.target.value }) : setNewUser({ ...newUser, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest flex items-center gap-2"><Globe size={10} /> Correo Electrónico</label>
                                        <input
                                            className="w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-white outline-none focus:border-emerald-500 focus:ring-4 ring-emerald-500/10 transition-all font-bold text-sm"
                                            value={editUser ? editUser.email : newUser.email}
                                            onChange={e => editUser ? setEditUser({ ...editUser, email: e.target.value }) : setNewUser({ ...newUser, email: e.target.value })}
                                        />
                                    </div>

                                    <div className="relative">
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest flex items-center gap-2"><Lock size={10} /> Contraseña</label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                placeholder={editUser ? "Dejar vacío para mantener" : "Mínimo 6 caracteres"}
                                                className="w-full bg-slate-900/50 border border-slate-800 pl-5 pr-12 py-4 rounded-2xl text-white outline-none focus:border-emerald-500 focus:ring-4 ring-emerald-500/10 transition-all font-bold text-sm"
                                                value={editUser ? (editUser.password || '') : newUser.password}
                                                onChange={e => editUser ? setEditUser({ ...editUser, password: e.target.value }) : setNewUser({ ...newUser, password: e.target.value })}
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-emerald-500 transition-colors">
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest">Confirmar Contraseña</label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Repetir contraseña"
                                            className="w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-white outline-none focus:border-emerald-500 focus:ring-4 ring-emerald-500/10 transition-all font-bold text-sm"
                                            value={editUser ? (editUser.confirmPassword || '') : newUser.confirmPassword}
                                            onChange={e => editUser ? setEditUser({ ...editUser, confirmPassword: e.target.value }) : setNewUser({ ...newUser, confirmPassword: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest flex items-center gap-2"><ShieldCheck size={10} /> Nivel de Acceso (Rol)</label>
                                        <select
                                            disabled={!canAssignRoles}
                                            className={cn("w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-white outline-none font-bold text-sm", canAssignRoles ? "cursor-pointer appearance-none" : "opacity-50 cursor-not-allowed")}
                                            value={editUser ? editUser.role : newUser.role}
                                            onChange={e => handleRoleChange(e.target.value)}
                                        >
                                            <option value="ADMINISTRADOR">Administrador (Control Total)</option>
                                            <option value="COORDINADOR">Coordinador (Gestión de Grupos)</option>
                                            <option value="EDITOR">Editor (Puede Capturar)</option>
                                            <option value="VISOR">Visor (Solo Lectura)</option>
                                        </select>
                                        {isViewerRole && (
                                            <p className="mt-2 text-[9px] font-bold leading-relaxed text-amber-300">
                                                El rol Visor sólo permite permisos de consulta. Cambia el rol general a Editor para habilitar captura o edición.
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest flex items-center gap-2"><Layers size={10} /> Alcance Organizacional</label>
                                        <select
                                            disabled={!canAssignScope}
                                            className={cn("w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-indigo-400 outline-none font-black text-sm", canAssignScope ? "cursor-pointer appearance-none" : "opacity-50 cursor-not-allowed")}
                                            value={editUser ? (editUser.org_scope || 'INSTANCE') : (newUser as any).org_scope || 'INSTANCE'}
                                            onChange={e => {
                                                const scope = e.target.value;
                                                if (editUser) {
                                                    setEditUser({ ...editUser, org_scope: scope, vicepresidency_id: scope === 'GLOBAL' ? null : editUser.vicepresidency_id, commission_id: scope === 'GLOBAL' ? null : editUser.commission_id });
                                                } else {
                                                    setNewUser({ ...newUser, org_scope: scope as any, vicepresidency_id: scope === 'GLOBAL' ? null : newUser.vicepresidency_id, commission_id: scope === 'GLOBAL' ? null : newUser.commission_id });
                                                }
                                            }}
                                        >
                                            <option value="GLOBAL">🌍 Director Nacional (Todo el sistema)</option>
                                            <option value="VP">📊 Vicepresidente (Su VP e Instancias)</option>
                                            <option value="COORDINATOR">🤝 Coordinador (Varios Grupos)</option>
                                            <option value="INSTANCE">🏢 Presidente de Instancia (Solo su área)</option>
                                        </select>
                                    </div>

                                    {(editUser?.org_scope || (newUser as any).org_scope) === 'COORDINATOR' ? (
                                        <div className="md:col-span-2 space-y-4">
                                            <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest">Vinculación Multi-Instancia</label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto p-5 bg-slate-900/50 border border-slate-800 rounded-[1.5rem] scrollbar-thin scrollbar-thumb-emerald-500/20">
                                                <div className="space-y-2">
                                                    <p className="text-[7px] font-black text-emerald-500 uppercase tracking-widest mb-2 border-b border-emerald-500/20 pb-1">Vicepresidencias</p>
                                                    {vps.map(vp => {
                                                        const currentList = editUser ? (editUser.accessible_vps || []) : (newUser.accessible_vps || []);
                                                        const isChecked = currentList.includes(vp.id);
                                                        return (
                                                            <label key={vp.id} className={cn("flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all border", isChecked ? "bg-emerald-500/10 border-emerald-500/30 text-white" : "hover:bg-white/5 border-transparent text-white/50")}>
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={!canAssignScope}
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        const newList = isChecked ? currentList.filter((id: string) => id !== vp.id) : [...currentList, vp.id];
                                                                        if (editUser) {
                                                                            vpsScopeTouchedRef.current = true;
                                                                            setEditUser({ ...editUser, accessible_vps: newList });
                                                                        } else setNewUser({ ...newUser, accessible_vps: newList });
                                                                    }}
                                                                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                                                                />
                                                                <span className="text-[10px] font-bold uppercase">{vp.name}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-[7px] font-black text-indigo-500 uppercase tracking-widest mb-2 border-b border-indigo-500/20 pb-1">Instancias</p>
                                                    {comms.map(c => {
                                                        const currentList = editUser ? (editUser.accessible_instances || []) : (newUser.accessible_instances || []);
                                                        const isChecked = currentList.includes(c.id);
                                                        return (
                                                            <label key={c.id} className={cn("flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all border", isChecked ? "bg-indigo-500/10 border-indigo-500/30 text-white" : "hover:bg-white/5 border-transparent text-white/50")}>
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={!canAssignScope}
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        const newList = isChecked ? currentList.filter((id: string) => id !== c.id) : [...currentList, c.id];
                                                                        if (editUser) {
                                                                            instancesScopeTouchedRef.current = true;
                                                                            setEditUser({ ...editUser, accessible_instances: newList });
                                                                        } else setNewUser({ ...newUser, accessible_instances: newList });
                                                                    }}
                                                                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                                                                />
                                                                <span className="text-[10px] font-bold uppercase">{c.name}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ) : ((editUser?.org_scope || (newUser as any).org_scope) === 'VP' || (editUser?.org_scope || (newUser as any).org_scope) === 'INSTANCE') && (
                                        <div className="md:col-span-2">
                                            <label className="text-[8px] font-black text-white/70 uppercase mb-2 block tracking-widest">Vinculación Principal</label>
                                            <select
                                                disabled={!canAssignScope}
                                                className={cn("w-full bg-slate-900/50 border border-slate-800 px-5 py-4 rounded-2xl text-emerald-400 outline-none font-black text-sm", canAssignScope ? "cursor-pointer appearance-none" : "opacity-50 cursor-not-allowed")}
                                                value={editUser ? (editUser.vicepresidency_id || editUser.commission_id || '') : (newUser.vicepresidency_id || newUser.commission_id || '')}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    const isVP = vps.some(v => v.id === val);
                                                    if (editUser) {
                                                        setEditUser({ ...editUser, vicepresidency_id: isVP ? val : null, commission_id: !isVP ? val : null });
                                                    } else {
                                                        setNewUser({ ...newUser, vicepresidency_id: isVP ? val : null, commission_id: !isVP ? val : null });
                                                    }
                                                }}
                                            >
                                                <option value="">SELECCIONAR VP O INSTANCIA</option>
                                                <optgroup label="--- VICEPRESIDENCIAS ---">
                                                    {vps.map(vp => <option key={vp.id} value={vp.id}>📊 {vp.name}</option>)}
                                                </optgroup>
                                                <optgroup label="--- INSTANCIAS ---">
                                                    {comms.map(c => <option key={c.id} value={c.id}>🏢 {c.name}</option>)}
                                                </optgroup>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                <div className="p-6 border border-slate-800 rounded-[2rem] bg-slate-900/30">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <p className="text-[9px] font-black text-white/60 uppercase mb-1 tracking-widest flex items-center gap-2"><Settings size={12} /> Módulos y Nivel de Permiso</p>
                                            <p className="text-[8px] text-slate-600 font-bold">CONFIGURACIÓN GRANULAR DE ACCESOS</p>
                                        </div>
                                    </div>
                                    {hasModulePermissionConflict && (
                                        <div role="alert" className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-[9px] font-bold leading-relaxed text-red-200">
                                            Inconsistencia heredada: el rol Visor contiene permisos Editor. Cambia cada módulo a Visor u OFF antes de guardar.
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {ALL_MODULES.map(mod => {
                                            const rawModules: string[] = editUser?.accessible_modules || (newUser as any).accessible_modules || [];
                                            const entry = rawModules.find((e: string) => normalize(parseModulePermission(e).module) === normalize(mod));
                                            const currentPerm = entry ? parseModulePermission(entry).permission : 'OFF';

                                            const handlePermChange = (newPermVal: string) => {
                                                const filtered = rawModules.filter((e: string) => normalize(parseModulePermission(e).module) !== normalize(mod));
                                                const newModules = newPermVal !== 'OFF' ? [...filtered, `${mod}:${newPermVal}`] : filtered;
                                                if (editUser) setEditUser({ ...editUser, accessible_modules: newModules });
                                                else setNewUser({ ...newUser, accessible_modules: newModules });
                                            };

                                            return (
                                                <div key={mod} className={cn(
                                                    "flex items-center justify-between gap-3 p-3 rounded-2xl border transition-all",
                                                    currentPerm === 'EDITOR' ? "bg-emerald-500/5 border-emerald-500/20" :
                                                    currentPerm === 'VISOR' ? "bg-indigo-500/5 border-indigo-500/20" :
                                                    "bg-slate-800/10 border-slate-700/20 opacity-60"
                                                )}>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {currentPerm === 'EDITOR' ? <Edit3 size={12} className="text-emerald-500 flex-shrink-0" /> :
                                                         currentPerm === 'VISOR' ? <Eye size={12} className="text-indigo-400 flex-shrink-0" /> :
                                                         <ShieldX size={12} className="text-slate-600 flex-shrink-0" />}
                                                        <span className={cn("text-[9px] font-black uppercase tracking-tight truncate",
                                                            currentPerm === 'EDITOR' ? "text-emerald-400" :
                                                            currentPerm === 'VISOR' ? "text-indigo-400" :
                                                            "text-white/30"
                                                        )}>{mod}</span>
                                                    </div>
                                                    <select
                                                        disabled={!canAssignScope}
                                                        value={currentPerm}
                                                        onChange={e => handlePermChange(e.target.value)}
                                                        className={cn(
                                                            "text-[8px] font-black uppercase tracking-wider px-2 py-1 rounded-lg border outline-none transition-all",
                                                            !canAssignScope && "opacity-50 cursor-not-allowed",
                                                            canAssignScope && "cursor-pointer",
                                                            currentPerm === 'EDITOR' ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                                                            currentPerm === 'VISOR' ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" :
                                                            "bg-slate-800 text-white/50 border-slate-700"
                                                        )}
                                                    >
                                                        <option value="OFF">🚫 OFF</option>
                                                        <option value="VISOR">👁️ VISOR</option>
                                                        <option value="EDITOR" disabled={isViewerRole}>✍️ EDITOR</option>
                                                    </select>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-8 md:p-10 pt-4 border-t border-slate-800/50 flex-shrink-0 flex gap-4 bg-slate-950 rounded-b-[2.5rem]">
                                <button
                                    onClick={handleCloseAttempt}
                                    disabled={isSaving}
                                    className="flex-1 bg-slate-900 border border-slate-800 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={editUser ? handleUpdate : handleCreate}
                                    disabled={isSaving || hasModulePermissionConflict}
                                    className="flex-[2] bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
                                >
                                    {isSaving ? (
                                        <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    ) : <Save size={16} />}
                                    {editUser ? 'Guardar Cambios' : 'Finalizar Registro'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Confirm Discard Modal */}
            {showConfirmDiscard && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-sm w-full text-center space-y-6 shadow-2xl">
                        <h4 className="text-white font-black uppercase tracking-wider text-lg">¿Descartar cambios?</h4>
                        <p className="text-slate-400 text-xs font-medium leading-relaxed">
                            Tienes cambios sin guardar en el formulario. Si cierras ahora, se perderán de forma permanente.
                        </p>
                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setShowConfirmDiscard(false)}
                                className="flex-1 bg-slate-800 border border-slate-700 text-white py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-700 transition-all min-h-[44px]"
                            >
                                Seguir Editando
                            </button>
                            <button
                                onClick={closeModal}
                                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-500 transition-all min-h-[44px]"
                            >
                                Descartar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagementModule;
